import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import HoneyAcornMark from "@/brand/HoneyAcornMark";

export default function Signup() {
  const { signUp, isSupabaseConfigured } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signUp(email, password, fullName);
      toast.success(
        "Account created. Check your inbox to confirm, then sign in.",
      );
      navigate("/login");
    } catch (err) {
      toast.error(err.message || "Sign up failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background px-6 py-12 sm:px-12"
      data-testid="signup-page"
    >
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 flex items-center gap-2.5">
          <HoneyAcornMark size={30} />
          <div>
            <div className="font-heading text-lg font-bold tracking-tight text-neutral-900">
              HoneyAcorn
            </div>
            <div className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-neutral-500">
              Childcare, connected
            </div>
          </div>
        </div>

        <h2 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Create your account
        </h2>
        <p className="mt-2 text-base text-neutral-500">
          Create an account with your work email. Your organization admin will
          assign your role before you can access the workspace.
        </p>

        {!isSupabaseConfigured && (
          <div className="mt-6 rounded-2xl border border-secondary/60 bg-secondary/20 p-4 text-sm text-neutral-800">
            Supabase is not configured yet. Sign-up will be available once your
            keys are set in <code>.env</code>.
          </div>
        )}

        <form
          onSubmit={onSubmit}
          className="mt-8 space-y-4"
          data-testid="signup-form"
        >
          <div>
            <label htmlFor="signup-name" className="mb-1.5 block text-sm font-medium text-neutral-800">
              Full name
            </label>
            <input
              id="signup-name"
              name="name"
              data-testid="signup-name-input"
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="name"
              className="w-full rounded-xl border border-neutral-200 bg-white/70 px-4 py-3 text-neutral-900 shadow-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div>
            <label htmlFor="signup-email" className="mb-1.5 block text-sm font-medium text-neutral-800">
              Email
            </label>
            <input
              id="signup-email"
              name="email"
              data-testid="signup-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="w-full rounded-xl border border-neutral-200 bg-white/70 px-4 py-3 text-neutral-900 shadow-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div>
            <label htmlFor="signup-password" className="mb-1.5 block text-sm font-medium text-neutral-800">
              Password
            </label>
            <input
              id="signup-password"
              name="password"
              data-testid="signup-password-input"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-xl border border-neutral-200 bg-white/70 px-4 py-3 text-neutral-900 shadow-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <button
            data-testid="signup-submit-button"
            type="submit"
            disabled={submitting || !isSupabaseConfigured}
            className="w-full rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Creating account…" : "Create account"}
          </button>
          <p className="text-center text-sm text-neutral-500">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="signup-to-login-link"
              className="font-medium text-primary-hover hover:underline"
            >
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
